import {
  useEffect,
  useState
} from "react";
import {
  Link
} from "react-router-dom";
import {
  ArrowLeft,
  Receipt,
  ExternalLink
} from "lucide-react";
import {
  getMyReceipts,
  getMyReceiptHtmlUrl
} from "../../../api/fees/feeApi";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table
} from "../../../components/ui";
import {
  useToast
} from "../../../context/ToastContext";

const formatMoney = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDate = (d) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const StudentMyReceipts = () => {
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);

  const toast = useToast();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await getMyReceipts();
        const list = res?.data || res || [];
        setReceipts(Array.isArray(list) ? list : list.receipts || []);
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to load receipts.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const receiptColumns = [
    {
      key: "receiptNo",
      header: "Receipt #",
      render: (r) => (
        <span className="font-mono text-xs font-medium text-neutral-800 dark:text-neutral-100">
          {r.receiptNumber || r.receiptNo || r._id?.slice(-8)}
        </span>
      ),
    },
    {
      key: "date",
      header: "Date",
      render: (r) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300">
          {formatDate(r.paidAt || r.paymentDate || r.createdAt)}
        </span>
      ),
    },
    {
      key: "mode",
      header: "Payment Mode",
      render: (r) => (
        <span className="text-xs text-neutral-600 dark:text-neutral-300 font-medium">
          {r.paymentMode || r.mode || r.paymentModeId?.name || "—"}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      render: (r) => (
        <span className="text-xs font-bold text-emerald-600">
          {formatMoney(r.amount || r.totalAmount || r.netAmount)}
        </span>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (r) => (
        <a
          href={getMyReceiptHtmlUrl(r._id)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 transition"
        >
          <span>View</span>
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-neutral-50/50 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/student-home">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100"
            >
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 p-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-sky-50 text-sky-600">
                <Receipt className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
                  Payment Receipts
                </CardTitle>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                  View and download historical fee receipts
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            

            <Table
              columns={receiptColumns}
              data={receipts}
              isLoading={loading}
              density="comfortable"
              emptyState={<span className="text-xs text-neutral-400 dark:text-neutral-500">No payment receipts available yet.</span>}
            />

            <div className="pt-2">
              <Link to="/student-panel/fees/ledger">
                <Button variant="outline" fullWidth className="py-2.5 text-xs font-semibold">
                  Back to Fee Ledger
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StudentMyReceipts;